import { useCallback } from "react";
import { type ApolloCache } from "@apollo/client";
import { useMutation } from "@apollo/client/react";
import {
  AddIssuePhotoFieldValueDocument,
  ClearIssueCustomFieldDocument,
  RemoveIssuePhotoFieldValueDocument,
  UpdateIssueAddressDocument,
  UpdateIssueAssigneesDocument,
  UpdateIssueDescriptionDocument,
  UpdateIssueDocument,
  UpdateIssueStageDocument,
  UpdateIssueValuesDocument,
  UpdateIssueWorkflowDocument,
} from "#app/lib/data";
import { useTrackedMutation, AnalyticsContext, IssueEvent } from "#app/lib/analytics";
import { identifyIssueInCache } from "#app/hooks/kanban/kanban-cache-utils";
import { toastMutationError } from "#app/lib/utils/toast-errors";

const issueEdit = (label: string) => (({
  context: AnalyticsContext.Issue,
  event: IssueEvent.Edit,
  label
}) as const);

// Commutative cache write for a photos-field add/remove. Mutates the Issue's
// `values` map in place via cache.modify (the add/remove mutations deliberately
// don't return `values`, so this is the ONLY writer). Because each call is a
// single-element delta — add dedupes, remove filters — it's idempotent and
// order-independent: optimistic layers stack cumulatively, and an out-of-order
// real response can't replace the field with a stale snapshot. Issue is keyed
// by `issueId` (see apollo-client.ts typePolicies).
function applyPhotoFieldDelta(
  cache: ApolloCache,
  args: { issueId: string; fieldId: string; photoId: string; op: "add" | "remove" },
): void {
  const cacheId = identifyIssueInCache(cache, args.issueId);
  if (!cacheId) return;

  cache.modify({
    id: cacheId,
    fields: {
      // Param typed `any`, mirroring connection-cache-utils — cache.modify's
      // Modifier signature doesn't accept a narrower object type here.
      values(existing: any) {
        const map: Record<string, unknown> = { ...(existing ?? {}) };
        const arr = Array.isArray(map[args.fieldId])
          ? (map[args.fieldId] as unknown[]).filter((v): v is string => typeof v === "string")
          : [];
        map[args.fieldId] =
          args.op === "add"
            ? arr.includes(args.photoId)
              ? arr
              : [...arr, args.photoId]
            : arr.filter((id) => id !== args.photoId);
        return map;
      },
    },
  });
}

/**
 * Centralizes all issue-view mutation hooks. Components destructure
 * only the mutations they need and pass their own variables/optimistic responses.
 *
 * Single-field renames (e.g. issue title) use the omnibus `updateIssue`
 * directly — it covers that path and returns the full IssueViewIssue response
 * shape.
 *
 * Photos fields are the exception to the "pass your own variables" rule:
 * `addPhotoFieldValue`/`removePhotoFieldValue` are higher-level helpers that
 * own their optimistic + commutative cache write, so concurrent uploads can't
 * clobber each other the way a per-field full-array overwrite would.
 */
export function useIssueViewMutations() {
  const [updateDescriptionMut] = useMutation(UpdateIssueDescriptionDocument);
  const [updateWorkflowMut] = useMutation(UpdateIssueWorkflowDocument);
  const [updateStageMut] = useMutation(UpdateIssueStageDocument);
  const [updateAddressMut] = useMutation(UpdateIssueAddressDocument);
  const [updateAssigneesMut] = useMutation(UpdateIssueAssigneesDocument);
  const [updateValuesMut] = useMutation(UpdateIssueValuesDocument);
  const [clearCustomFieldMut] = useMutation(ClearIssueCustomFieldDocument);
  const [addPhotoMut] = useMutation(AddIssuePhotoFieldValueDocument);
  const [removePhotoMut] = useMutation(RemoveIssuePhotoFieldValueDocument);
  const [updateIssueMut] = useMutation(UpdateIssueDocument);

  const updateDescription = useTrackedMutation(updateDescriptionMut, issueEdit("update_description"));
  const updateWorkflow = useTrackedMutation(updateWorkflowMut, issueEdit("update_workflow"));
  const updateStage = useTrackedMutation(updateStageMut, issueEdit("update_stage"));
  const updateAddress = useTrackedMutation(updateAddressMut, issueEdit("update_address"));
  const updateAssignees = useTrackedMutation(updateAssigneesMut, issueEdit("update_assignees"));
  const updateValues = useTrackedMutation(updateValuesMut, issueEdit("update_values"));
  const clearCustomField = useTrackedMutation(clearCustomFieldMut, issueEdit("clear_custom_field"));
  const addPhotoTracked = useTrackedMutation(addPhotoMut, issueEdit("add_photo_field_value"));
  const removePhotoTracked = useTrackedMutation(removePhotoMut, issueEdit("remove_photo_field_value"));
  const updateIssue = useTrackedMutation(updateIssueMut, issueEdit("update_issue"));

  const addPhotoFieldValue = useCallback(
    async (issueId: string, fieldId: string, photoId: string) => {
      try {
        await addPhotoTracked({
          variables: { issueId, fieldId, photoId },
          optimisticResponse: {
            addIssuePhotoFieldValue: {
              __typename: "Issue",
              id: `Issue:${issueId}`,
              issueId,
            },
          },
          update: (cache) => applyPhotoFieldDelta(cache, { issueId, fieldId, photoId, op: "add" }),
        });
      } catch (err) {
        toastMutationError(err, "Failed to add photo");
      }
    },
    [addPhotoTracked],
  );

  const removePhotoFieldValue = useCallback(
    async (issueId: string, fieldId: string, photoId: string) => {
      try {
        await removePhotoTracked({
          variables: { issueId, fieldId, photoId },
          optimisticResponse: {
            removeIssuePhotoFieldValue: {
              __typename: "Issue",
              id: `Issue:${issueId}`,
              issueId,
            },
          },
          update: (cache) => applyPhotoFieldDelta(cache, { issueId, fieldId, photoId, op: "remove" }),
        });
      } catch (err) {
        toastMutationError(err, "Failed to remove photo");
      }
    },
    [removePhotoTracked],
  );

  return {
    updateDescription,
    updateWorkflow,
    updateStage,
    updateAddress,
    updateAssignees,
    updateValues,
    clearCustomField,
    addPhotoFieldValue,
    removePhotoFieldValue,
    updateIssue,
  };
}
